import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, Trophy } from "lucide-react";

const MicrosoftIcon = ({ className }) => (
  <svg viewBox="0 0 23 23" className={className} aria-hidden="true">
    <path fill="#f35325" d="M1 1h10v10H1z" /><path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" /><path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const ssoParam = new URLSearchParams(window.location.search).get("sso");
  const ssoError = ssoParam
    ? ({ expired: "Your sign-in link expired — please try again.", denied: "Microsoft sign-in was cancelled or denied.", error: "Microsoft sign-in failed. Please try again." }[ssoParam] || "Sign-in failed.")
    : "";

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[hsl(var(--sidebar))] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(-45deg, #fff 0, #fff 1px, transparent 1px, transparent 22px)" }} />
        <img src="/monge-logo-footer.svg" alt="" aria-hidden="true" className="pointer-events-none select-none absolute -bottom-8 -right-16 w-[130%] max-w-none opacity-[0.06] -rotate-[8deg]" />
        <div className="relative flex items-center gap-3">
          <img src="/monge-logo-footer.svg" alt="Monge & Associates" className="h-8 w-auto" />
        </div>
        <div className="relative">
          <Trophy className="h-8 w-8 text-primary mb-5" />
          <h1 className="font-display font-extrabold tracking-tight text-5xl leading-[0.95]">
            Attorneys<br />Who Win.
          </h1>
          <p className="text-white/60 mt-5 max-w-md text-sm leading-relaxed">
            Authorized personnel only. All activity is monitored for confidentiality compliance.
          </p>
        </div>
        <div className="relative flex flex-col gap-1 text-xs text-white/45">
          <span className="flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> Restricted · Internal access only · DLP protected</span>
          <span>Atlanta, GA · Est. 1993 · <span className="font-condensed font-semibold tracking-wide">(888) 477-0597</span></span>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <img src="/monge-logo.svg" alt="Monge & Associates" className="h-9 w-auto" />
          </div>
          <h2 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Sign in to the Hub</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Use your Monge &amp; Associates Office 365 account.</p>

          {ssoError && <div data-testid="sso-error" className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2 rounded-sm">{ssoError}</div>}

          <Button type="button" data-testid="entra-login-btn" onClick={() => { window.location.href = `${BACKEND}/api/auth/entra/login`; }} className="w-full h-12 font-semibold text-base">
            <MicrosoftIcon className="h-5 w-5 mr-2" /> Continue with Microsoft 365
          </Button>

          <div className="mt-8 flex items-start gap-2 text-xs text-slate-500 bg-red-50 border border-red-100 p-3 rounded-sm">
            <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span>Access is restricted to Monge &amp; Associates staff. All sign-ins use single sign-on (Office 365) and are logged for security.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
